import React, { useEffect } from 'react';
import { Box, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LegalFooter from './LegalFooter';

// 法務3ページの共通レイアウト。背景・枠線は PreviewRaceDashboardPage と同じ値を使い、
// 未ログインで直接開かれてもアプリの一部として見えるようにする。
function LegalPageLayout({ title, lastUpdated, children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 0%, rgba(43,132,255,0.22), transparent 28%), radial-gradient(circle at 90% 10%, rgba(50,255,226,0.14), transparent 24%), linear-gradient(180deg, #02060d 0%, #07111d 46%, #03070f 100%)',
        color: '#eef8ff',
        fontFamily: '"Avenir Next", "Yu Gothic", "Hiragino Sans", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}
          >
            <Typography
              sx={{
                color: '#71dbff',
                fontSize: 12,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
              }}
            >
              ちりり競馬
            </Typography>
            <MuiLink
              component={RouterLink}
              to="/lp"
              underline="hover"
              sx={{ fontSize: 13, color: 'rgba(196, 224, 243, 0.68)', '&:hover': { color: '#8fd8ff' } }}
            >
              サービス紹介に戻る
            </MuiLink>
          </Stack>

          <Paper
            sx={{
              p: { xs: 2.5, sm: 4 },
              borderRadius: 4,
              color: 'inherit',
              border: '1px solid rgba(92, 160, 219, 0.2)',
              background: 'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
            }}
          >
            <Typography component="h1" sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 800 }}>
              {title}
            </Typography>
            {lastUpdated && (
              <Typography sx={{ mt: 1, fontSize: 13, color: 'rgba(196, 224, 243, 0.62)' }}>
                最終改定日：{lastUpdated}
              </Typography>
            )}

            <Box sx={{ mt: 3 }}>{children}</Box>
          </Paper>

          {/* 法務ページ同士も相互に行き来できるようにする（フッターと同じリンク集を再利用） */}
          <LegalFooter sx={{ borderTop: 'none', pt: 0 }} />
        </Stack>
      </Box>
    </Box>
  );
}

// 各ページの見出し。条番号を持つ規約と、見出しだけの表記ページで共用する。
// mt は無条件に付ける。以前は :first-of-type で先頭だけ 0 にしていたが、
// 規約・PP はどちらも導入文の <Typography> が先に来るため、第1条だけが
// 導入文に貼り付いて表示されていた。
export function LegalSection({ heading, children }) {
  return (
    <Box component="section" sx={{ mt: 3.5 }}>
      <Typography
        component="h2"
        sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, color: '#bfe6ff' }}
      >
        {heading}
      </Typography>
      <Box sx={{ mt: 1.25, color: 'rgba(216, 236, 250, 0.86)', fontSize: 14, lineHeight: 1.9 }}>
        {children}
      </Box>
    </Box>
  );
}

// 条文中の箇条書き。番号付き・番号なしのどちらでも同じ余白にする。
export function LegalList({ items, ordered = true }) {
  return (
    <Box
      component={ordered ? 'ol' : 'ul'}
      sx={{ pl: 3, m: 0, '& li': { mt: 0.75 }, '& li:first-of-type': { mt: 0 } }}
    >
      {items.map((item, index) => (
        // 条文は静的な配列なので index を key にしてよい（並び替え・削除が起きない）
        <li key={index}>{item}</li>
      ))}
    </Box>
  );
}

export default LegalPageLayout;
